import { useEffect } from 'react';
import { MapPin, Calendar, Search, Compass, ArrowLeft } from 'lucide-react';

interface AboutPageProps {
  onBack: () => void;
}

export default function AboutPage({ onBack }: AboutPageProps) {
  useEffect(() => {
    const prevTitle = document.title;
    const descEl = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const prevDesc = descEl?.content ?? '';
    document.title = 'About Get Out Pitt — Pitt Fest Pittsburgh & Pitt Parties';
    descEl?.setAttribute('content', "Learn about Get Out Pitt, your free guide to Pitt Fest Pittsburgh events, Pitt parties, and things to do in Pittsburgh, PA — an interactive event discovery map covering concerts, festivals, outdoor activities, and more.");
    return () => {
      document.title = prevTitle;
      descEl?.setAttribute('content', prevDesc);
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-zinc-950 text-white z-20 border-b border-zinc-800/80">
        <div className="px-4 sm:px-6 py-3 flex items-center gap-4">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-zinc-400 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <span className="text-[17px] font-bold tracking-tight leading-none">
            Get Out <span className="text-amber-400">Pitt</span>
          </span>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-12">
        {/* Hero */}
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-slate-900 mb-3">About Get Out Pitt — Pitt Fest Pittsburgh &amp; Pitt Parties</h1>
          <p className="text-slate-500 text-lg leading-relaxed">
            Your local guide to what's happening in and around Pittsburgh — from live music
            and outdoor adventures to food festivals and family events.
          </p>
        </div>

        {/* Mission */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold text-slate-800 mb-3">Our Mission</h2>
          <p className="text-slate-600 leading-relaxed">
            Pittsburgh has an incredibly vibrant events scene, but finding what's on can be
            scattered across dozens of websites and social media pages. Get Out Pitt pulls
            it all together in one place — an interactive map that shows you real events
            happening near you, right now and in the days ahead.
          </p>
        </section>

        {/* Features */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold text-slate-800 mb-5">What We Offer</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              {
                Icon: MapPin,
                title: 'Interactive Map',
                desc: 'Explore events pinned to a live map. Click any marker to see details, dates, and ticket links.',
              },
              {
                Icon: Search,
                title: 'Smart Search',
                desc: 'Search by keyword to instantly find concerts, festivals, markets, or anything else you\'re looking for.',
              },
              {
                Icon: Calendar,
                title: 'Up-to-Date Listings',
                desc: 'Events are automatically discovered and refreshed so the listings stay current.',
              },
              {
                Icon: Compass,
                title: 'Near You',
                desc: 'Use your location to center the map on where you are and discover what\'s close by.',
              },
            ].map(({ Icon, title, desc }) => (
              <div key={title} className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center mb-3">
                  <Icon className="w-4.5 h-4.5 text-amber-500" />
                </div>
                <h3 className="font-semibold text-slate-800 text-sm mb-1">{title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Disclaimer */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold text-slate-800 mb-3">About Our Listings</h2>
          <p className="text-slate-600 leading-relaxed text-sm">
            Event information on Get Out Pitt is aggregated from publicly available sources.
            While we strive to keep listings accurate, details like times, locations, and
            ticket prices may change. We recommend confirming details directly with event
            organizers before attending.
          </p>
        </section>

        {/* Location */}
        <section>
          <h2 className="text-lg font-semibold text-slate-800 mb-3">Coverage Area</h2>
          <p className="text-slate-600 leading-relaxed text-sm">
            Get Out Pitt is focused on the greater Pittsburgh, Pennsylvania area — including
            neighborhoods, surrounding boroughs, and regional events within a 60-mile radius
            of downtown Pittsburgh.
          </p>
        </section>
      </main>

      <footer className="border-t border-slate-200 py-6 text-center text-xs text-slate-400">
        <p>&copy; {new Date().getFullYear()} Get Out Pitt. All rights reserved.</p>
      </footer>
    </div>
  );
}
